import { makeStyles } from '@material-ui/core';
import { yellow } from '@material-ui/core/colors';
import classNames from 'classnames';
import Moment from 'moment-timezone';
import React from 'react';
import { LabelProps, TooltipProps } from 'recharts';

import { PostType } from '../../../graphql/gen/types';
import { colorPalette, TextSizesEnum } from '../../../theme';
import { PlexTypography } from '../../shared/primitives/PlexTypography';

const postColor = colorPalette.purpleGray._400;
const eventColor = yellow.A700;
const milestoneColor = 'black';
export const rectangleWidth = 150;

type ExpandedLabelProps = {
  title: string;
  date: string;
  id: string;
  type: PostType;
  color: string | undefined;
};

const ExpandedLabelReact = (props: ExpandedLabelProps): React.ReactElement => {
  const classes = useStylesLabel(props);
  const { title, date, id, type } = props;
  return (
    <a className={classes.box} href={`#timeline-${id}`}>
      {type === PostType.Post && <div className={classNames(classes.icon, classes.postIcon)} />}
      {type === PostType.Event && <div className={classNames(classes.icon, classes.eventIcon)} />}
      {type === PostType.Milestone && (
        <svg className={classNames(classes.icon, classes.milestoneIcon)}>
          {milestoneShape(5, 3, props.color || milestoneColor)}
        </svg>
      )}
      <div className={classes.boxContent}>
        <PlexTypography className={classes.boxTitle} size={TextSizesEnum.XSmall} text={title} />
        <PlexTypography
          className={classes.boxDate}
          size={TextSizesEnum.XSmall}
          text={Moment(date).format('hA - MMM DD')}
        />
      </div>
    </a>
  );
};

export const postShape = (x: string | number, y: string | number, color: string): React.ReactElement => (
  <circle cx={x} cy={y} fill={color} stroke={'#fff'} r="4" />
);

export const eventShape = (x: string | number, y: string | number, color: string): React.ReactElement => (
  <polygon
    transform={`translate(${x}, ${y})`}
    points={`-${rhombusRadius},0 0,${rhombusRadius} ${rhombusRadius},0 0, -${rhombusRadius}`}
    fill={color}
    stroke={'#fff'}
  />
);

export const milestoneShape = (x: string | number, y: string | number, color: string): React.ReactElement => (
  <polygon transform={`translate(${x}, ${y})`} points="0 -4.5, 5 4, -5 4" fill={color} stroke={'#fff'} />
);

export const ShapeExpanded = (props: LabelProps & ExpandedLabelProps): React.ReactElement => {
  // this is actually not a react component, so can't use useStyles(). Will just use inlineStyles.
  const { x, y, title, date, id, type, color } = props;

  return (
    // Important to position this via `transform: translate(x, y)`, see https://github.com/all-hands-hq/source/issues/2162
    <foreignObject transform={`translate(${x}, ${y})`} width={rectangleWidth} height={38}>
      <ExpandedLabelReact title={title} date={date} id={id} type={type} color={color} />
    </foreignObject>
  );
};

const rhombusRadius = 6;
export const ShapeCollapsed = (props: LabelProps & ExpandedLabelProps): React.ReactElement<SVGElement> => {
  // this is actually not a react component, so can't use useStyles(). Will just use inlineStyles.
  const { x, y, type, color } = props;
  if (!x || !y) {
    throw new Error(`Invalid coordinate (${x}, ${y})`);
  }
  switch (type) {
    case PostType.Post:
      return postShape(x, y, color || postColor);
    case PostType.Event:
      return eventShape(x, y, color || eventColor);
    case PostType.Milestone:
      return milestoneShape(x, y, color || milestoneColor);
    case PostType.Task:
      throw new Error(`Task cannot be displayed on timeline`);
  }
};

export const CustomTooltip = (props: TooltipProps<never, never>): React.ReactElement | null => {
  if (!props?.payload || !props?.payload[0]?.payload) {
    return null;
  }
  const { title, date, id, expanded, type, color } = props.payload[0].payload;
  if (expanded) {
    return null;
  }
  return <ExpandedLabelReact title={title} date={date} id={id} type={type} color={color} />;
};

const useStylesLabel = makeStyles((theme) => ({
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: '20px 0',
  },
  box: {
    padding: '3px 8px',
    backgroundColor: '#fff',
    borderRadius: 6,
    display: 'flex',
    flexDirection: 'row',
    width: rectangleWidth,
    height: 38,
    textDecoration: 'none',
  },
  boxContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  boxTitle: {
    color: colorPalette.purpleGray._400,
    overflow: 'hidden',
  },
  boxDate: {
    color: colorPalette.purpleGray._300,
  },
  eventIcon: {
    backgroundColor: (props: ExpandedLabelProps): string => props.color || eventColor,
    transform: 'rotate(45deg)',
  },
  postIcon: {
    backgroundColor: (props: ExpandedLabelProps): string => props.color || postColor,
    borderRadius: 4,
  },
  milestoneIcon: {
    overflow: 'overlay',
  },
  icon: {
    width: 8,
    height: 8,
    marginTop: 5,
    marginRight: 5,
  },
}));
